import React, { forwardRef } from 'react';

import type { InputBaseProps, Theme } from '@mui/material';
import { alpha, FormHelperText, InputBase, useTheme } from '@mui/material';
import clsx from 'clsx';
import type { StandardLonghandProperties } from 'csstype';
import { SelectButton } from 'src/components/generic/SelectButton';
import { StepperDownIcon } from 'src/components/icons/StepperDownIcon';
import { StepperUpIcon } from 'src/components/icons/StepperUpIcon';
import { doNothing } from 'src/helpers/utils';
import { alineDefaultFontFamily } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

interface StyleProps {
  error: boolean;
  disabled?: boolean;
}

/**
 * Function to return consistent placeholder CSS props for input/textarea.
 */
export function getAlinePlaceholderCSSProps(theme: Theme): React.CSSProperties {
  return {
    color: alpha(theme.palette.text.disabled, 0.4),
    fontFamily: alineDefaultFontFamily,
    opacity: 'unset',
  };
}

/**
 * NOTE: AlineInput is in src so it is executed *VERY EARLY* and any override classes take precedence.
 * See https://v4.mui.com/styles/advanced/#makestyles-withstyles-styled for more info.
 */
const useStyles = makeStyles<StyleProps>()((theme, { disabled, error }) => ({
  fullWidth: {
    width: '100%',
  },
  input: {
    '&:focus-within': {
      border: error
        ? `1px solid ${theme.palette.error.main}`
        : `1px solid ${theme.appColors.primary}`,
    },
    border: error ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    boxSizing: 'border-box',
    display: 'flex',
    minHeight: theme.appSizes.inputHeight,
    position: 'relative',
  },
  inputBase: {
    '&::placeholder': {
      ...getAlinePlaceholderCSSProps(theme),
    },
    color: disabled ? theme.palette.text.disabled : theme.appColors.iconsType,
    fontSize: 14,
    padding: theme.spacing(1),
  },
  inputBaseRoot: {
    width: '100%',
  },
  inputWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  stepper: {
    height: 8,
    width: 8,
  },
  stepperWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
}));

export interface AlineInputProps extends InputBaseProps {
  alignHelperText?: StandardLonghandProperties['textAlign'];
  dataCy?: string;
  error?: boolean;
  helperText?: string;
  hideHelperText?: boolean;
  inputClassName?: string;
  showSteppers?: boolean;
  stepDown?: (value: unknown) => void;
  stepUp?: (value: unknown) => void;
  wrapperRef?: React.Ref<HTMLDivElement>;
}

export const AlineInput = forwardRef(function AlineInput(props: AlineInputProps, ref): JSX.Element {
  const {
    autoComplete = 'off',
    className,
    error,
    fullWidth = true,
    helperText,
    hideHelperText = false,
    classes: inputBaseClasses,
    inputClassName,
    stepDown = doNothing,
    stepUp = doNothing,
    wrapperRef,
    type = 'text',
    showSteppers = type === 'number',
    dataCy = 'aline-input',
    alignHelperText = 'left',
    'aria-label': ariaLabel,
    inputProps: inputPropsProp,
    ...inputProps
  } = props;

  const theme = useTheme();
  const { classes } = useStyles({ disabled: inputProps.disabled, error: !!error });

  return (
    <div
      ref={wrapperRef}
      className={clsx(classes.inputWrapper, className, fullWidth && classes.fullWidth)}
    >
      <div className={clsx(classes.input, inputClassName)}>
        <InputBase
          ref={ref}
          autoComplete={autoComplete}
          className={classes.inputBaseRoot}
          classes={{
            ...inputBaseClasses,
            input: clsx(classes.inputBase, inputBaseClasses && inputBaseClasses.input),
          }}
          fullWidth={fullWidth}
          inputProps={{ 'aria-label': ariaLabel, ['data-cy']: dataCy, ...inputPropsProp }}
          type={type === 'number' ? 'text' : type}
          {...inputProps}
        />
        {type === 'number' && showSteppers && (
          <div className={classes.stepperWrapper}>
            <SelectButton
              disabled={inputProps.disabled}
              style={{ flexGrow: 1, padding: theme.spacing(0.5, 0.5, 0) }}
              onClick={() => stepUp(inputProps.value)}
            >
              <StepperUpIcon className={classes.stepper} />
            </SelectButton>
            <SelectButton
              disabled={inputProps.disabled}
              style={{ flexGrow: 1, padding: theme.spacing(0, 0.5, 0.5) }}
              onClick={() => stepDown(inputProps.value)}
            >
              <StepperDownIcon className={classes.stepper} />
            </SelectButton>
          </div>
        )}
      </div>
      {helperText && !hideHelperText && (
        <FormHelperText error={error} style={{ textAlign: alignHelperText }}>
          {helperText}
        </FormHelperText>
      )}
    </div>
  );
});
